// Primary Conversion Selection — Conversions section, deterministic check.
// Surfaces conversion actions that are included in the Conversions metric (so Smart Bidding
// optimizes to them) but are NOT marked primary. High-volume secondaries dilute the bidding
// signal — that's what we want the auditor to see immediately.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';

interface ActionRow {
  id?: string;
  name?: string;
  category?: string;
  primaryForGoal: boolean;
  includeInConversionsMetric: boolean;
  last30dConversions: number;
}

interface PrimaryConversionShape {
  totalEnabled: number;
  primaryCount: number;
  secondaryInMetricCount: number;
  highVolumeThreshold: number;
  largeVolumeThreshold: number;
  flagged: ActionRow[];
  enabledActions: ActionRow[];
}

const CATEGORY_LABEL: Record<string, string> = {
  PURCHASE: 'Purchase',
  SIGNUP: 'Sign-up',
  LEAD: 'Lead',
  ADD_TO_CART: 'Add to Cart',
  BEGIN_CHECKOUT: 'Begin Checkout',
  SUBSCRIBE_PAID: 'Paid Subscription',
  PAGE_VIEW: 'Page View',
  CONTACT: 'Contact',
  DOWNLOAD: 'Download',
  SUBMIT_LEAD_FORM: 'Submit Lead Form',
  BOOK_APPOINTMENT: 'Book Appointment',
  REQUEST_QUOTE: 'Request Quote',
  GET_DIRECTIONS: 'Get Directions',
  OUTBOUND_CLICK: 'Outbound Click',
  STORE_VISIT: 'Store Visit',
  STORE_SALE: 'Store Sale',
  QUALIFIED_LEAD: 'Qualified Lead',
  CONVERTED_LEAD: 'Converted Lead'
};

function categoryLabel(c: string | undefined): string {
  if (!c) return '—';
  return CATEGORY_LABEL[c] ?? c;
}

function formatVolume(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return n.toLocaleString();
}

export const PrimaryConversionEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as PrimaryConversionShape;
  const hasFlags = e.flagged.length > 0;

  return (
    <div className='evidence'>
      <p className='evidence__lead'>
        {hasFlags ? (
          <>
            <strong>{e.flagged.length}</strong> conversion action
            {e.flagged.length === 1 ? '' : 's'} included in the &ldquo;Conversions&rdquo; column
            (the metric Smart Bidding optimizes to) {e.flagged.length === 1 ? 'is' : 'are'} firing
            meaningfully but not marked <em>primary for goal</em> — at this volume, that dilutes the
            bidding signal.
          </>
        ) : (
          <>
            All enabled conversion actions counted in the &ldquo;Conversions&rdquo; column (the
            metric Smart Bidding optimizes to) are either marked primary or below the
            meaningful-volume threshold ({e.highVolumeThreshold}/30d).
          </>
        )}
      </p>

      {hasFlags && (
        <section className='evidence__section'>
          <h4 className='evidence__section-title'>
            Flagged actions
            <span className='evidence__section-tag evidence__section-tag--actionable'>
              {e.flagged.length} to review
            </span>
          </h4>
          <ActionTable rows={e.flagged} />
        </section>
      )}

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          All enabled conversion actions
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalEnabled} enabled · {e.primaryCount} primary
          </span>
        </h4>
        <ActionTable
          rows={e.enabledActions}
          highlightFlagged
          highVolumeThreshold={e.highVolumeThreshold}
        />
      </section>

      <p className='evidence__footnote'>
        Volume is last 30 days. Thresholds: ≥ {e.highVolumeThreshold}/30d meaningful, ≥{' '}
        {e.largeVolumeThreshold}/30d significant.
      </p>
    </div>
  );
};

const ActionTable = ({
  rows,
  highlightFlagged,
  highVolumeThreshold
}: {
  rows: ActionRow[];
  highlightFlagged?: boolean;
  highVolumeThreshold?: number;
}) => {
  if (rows.length === 0) {
    return <p className='evidence__empty'>No actions to display.</p>;
  }
  return (
    <table className='evidence__table evidence__table--primary-conversion'>
      <thead>
        <tr>
          <th>Conversion action</th>
          <th>Category</th>
          <th>Role</th>
          <th>Include in &ldquo;Conversions&rdquo;</th>
          <th className='evidence__th--right'>Conv (30d)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => {
          const isFlagged =
            highlightFlagged === true &&
            r.includeInConversionsMetric &&
            !r.primaryForGoal &&
            (highVolumeThreshold ?? Infinity) <= r.last30dConversions;
          return (
            <tr
              key={r.id ?? r.name}
              className={classNames({ 'evidence__row--flagged': isFlagged })}
            >
              <td>
                <div>{r.name ?? '(unnamed)'}</div>
                {r.id && <div className='evidence__action-id'>{r.id}</div>}
              </td>
              <td>{categoryLabel(r.category)}</td>
              <td>
                <span
                  className={classNames(
                    'evidence__pill',
                    r.primaryForGoal ? 'evidence__pill--primary' : 'evidence__pill--secondary'
                  )}
                >
                  {r.primaryForGoal ? 'Primary' : 'Secondary'}
                </span>
              </td>
              <td>
                <span
                  className={classNames(
                    'evidence__pill',
                    r.includeInConversionsMetric
                      ? 'evidence__pill--in-metric'
                      : 'evidence__pill--excluded'
                  )}
                >
                  {r.includeInConversionsMetric ? 'Counted' : 'Excluded'}
                </span>
              </td>
              <td className='evidence__td--right'>{formatVolume(r.last30dConversions)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
